import { createSelector } from 'reselect';
import dayjs from 'dayjs';
// Models
import { RootState } from 'store';
import IQuiz from 'models/Quiz';
import IPage from 'models/Page';
// Types
import PageTypes from 'types/PageTypes';
// Utilities
import { quizTypesLabels } from 'utilities/utilities';

export const selectQuizzes = (state:RootState) => state.quizzes.quizzes;
export const selectActiveQuizzes = (state:RootState) => state.quizzes.activeQuizzes;
export const selectQuiz = (state:RootState) => state.quizzes.quiz;
export const selectCheckedQuizzesIds = (state:RootState) => state.quizzes.checkedQuizzesIds;
export const selectFilter = (state:RootState) => state.quizzes.filter;
export const selectLoading = (state:RootState) => state.quizzes.loading;

export const selectFilteredQuizzes = createSelector(
  [
    selectQuizzes, selectFilter
  ],
  (quizItems:Array<IQuiz> | null, filter:{ search:string }) => {
    if ( !quizItems ) return null;
    const { search } = filter;
    const searchToLower = search.toLowerCase();
    const quizzes = quizItems.map((quiz) => ({
      ...quiz,
      createdByName: quiz.createdBy ? `${quiz.createdBy.name} ${quiz.createdBy.surname}` : '',
      category: quiz.namespace ? quiz.namespace.name : '',
      quizTypeLabel: quizTypesLabels[quiz.type],
    }));
    if ( !searchToLower ) return quizzes;
    return quizzes.filter((quizItem:IQuiz) => {
      const hasName = quizItem.quizName.toLowerCase().includes(searchToLower);
      const hasNamespace = !!quizItem.namespace?.slug?.toLowerCase().includes(searchToLower)
      return hasName || hasNamespace;
    });
  }
);

export const selectIsStartNowVisible = createSelector(
  [
    selectQuizzes,
    (_, props:{ quizId:string }) => props
  ],
  (quizzes:IQuiz[] | null, { quizId }) => {
    if ( !quizzes ) return false;
    const quiz = quizzes.find((quiz:IQuiz) => quiz._id === quizId);
    if ( !quiz ) return false;
    if ( !quiz.startsDate && !quiz.endsDate ) return true;
    const now = dayjs();
    const startDate = dayjs(Number(quiz.startsDate));
    return now.isBefore(startDate);
  }
);

export const selectIsEndNowVisible = createSelector(
  [
    selectQuizzes,
    (_, props:{ quizId:string }) => props
  ],
  (quizzes:IQuiz[] | null, { quizId }) => {
    if ( !quizzes ) return false;
    const quiz = quizzes.find((quiz:IQuiz) => quiz._id === quizId);
    if ( !quiz || !quiz.startsDate ) return false;
    const now = dayjs();
    const startDate = dayjs(Number(quiz.startsDate));
    const endDate = quiz.endsDate ? dayjs(Number(quiz.endsDate)) : null;

    return now.isAfter(startDate) && ( !endDate || now.isBefore(endDate) );
  }
);

export const selectQuestions = createSelector(
  [ selectQuiz ],
  (quiz) => {
    if (!quiz || !quiz.pages) return [];
    const questionsRefs = quiz.pages?.reduce((acc:string[], cur:IPage) => {
      if (cur.type === PageTypes.Question) return [...acc, cur.refs[0]];
      return acc;
    }, []);
    return quiz.questions.filter(question => questionsRefs.includes(question.ref));
  }
);

export const selectAdditionalQuestions = createSelector(
  [ selectQuiz ],
  (quiz) => {
    if (!quiz || !quiz.pages) return [];
    const additionalQuestions = quiz.pages?.reduce((acc:string[], cur:IPage) => {
      if (cur.type === PageTypes.AdditionalQuestions) return [...acc, ...cur.refs];
      return acc;
    }, []);
    return quiz.questions.filter(question => additionalQuestions.includes(question.ref));
  }
);
